/**
 * 导师一次性反馈页（扩展⑥）：公开免登录，由 `/feedback/:token` 访问。
 * - 打开即见**脱敏**队伍概要（队名/赛事/角色分工，无成员姓名与联系方式）；
 * - 提交 ≤500 字反馈后令牌即焚（每个链接只接受一次）；
 * - AI 行动建议由队伍侧生成（原文永不被改写），导师不感知、不依赖。
 */
import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';

import { advisorFeedbackApi } from '../api/advisor-feedback.js';
import { toUserMessage } from '../api/errors.js';
import { Button } from '../components/Button.js';
import { Card } from '../components/Card.js';
import { Loading } from '../components/Loading.js';
import { Tag } from '../components/Tag.js';

export function AdvisorFeedbackPage() {
  const { token } = useParams<{ token: string }>();
  const [content, setContent] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const contextQuery = useQuery({
    queryKey: ['advisor-feedback-context', token ?? ''],
    queryFn: () => advisorFeedbackApi.getByToken(token ?? ''),
    enabled: typeof token === 'string' && token.length > 0,
    retry: false,
  });

  const submit = useMutation({
    mutationFn: (text: string) => advisorFeedbackApi.submitByToken(token ?? '', text),
    onSuccess: () => {
      setDone(true);
      setFormError(null);
    },
    onError: (error) => {
      setFormError(toUserMessage(error));
    },
  });

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const text = content.trim();
    if (text.length === 0) {
      setFormError('请填写反馈内容（500 字以内）');
      return;
    }
    if (text.length > 500) {
      setFormError(`反馈内容过长：当前 ${text.length} 字，上限 500 字`);
      return;
    }
    setFormError(null);
    submit.mutate(text);
  };

  if (typeof token !== 'string' || token.length === 0) {
    return (
      <div className="auth-shell">
        <Card className="auth-card" title="链接无效">
          <p className="muted">反馈链接缺少令牌参数，请联系队伍队长重新生成。</p>
        </Card>
      </div>
    );
  }

  if (contextQuery.isPending) {
    return (
      <div className="auth-shell">
        <Card className="auth-card" title="正在打开反馈链接…">
          <Loading label="正在读取队伍概要" rows={2} />
        </Card>
      </div>
    );
  }

  if (contextQuery.isError || contextQuery.data === undefined) {
    return (
      <div className="auth-shell">
        <Card className="auth-card" title="链接已失效">
          <p className="muted wrap-anywhere">{toUserMessage(contextQuery.error)}</p>
          <p className="field-hint">
            每个链接 7 天内有效且只接受一次反馈。如需再次指导，请让队长在队伍「工作台」重新生成链接。
          </p>
          <p className="auth-foot">
            <Link to="/">了解 CrossMatch</Link>
          </p>
        </Card>
      </div>
    );
  }

  const context = contextQuery.data;

  if (done) {
    return (
      <div className="auth-shell">
        <Card className="auth-card" title="反馈已收到，谢谢老师！">
          <p>
            您对「{context.teamName}」的反馈已送达，队伍会在其工作台第一时间看到您的原话。
          </p>
          <p className="field-hint">本链接已完成使命并失效——如需补充意见，请让队长生成新链接。</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="auth-shell">
      <Card
        className="auth-card"
        title={`给「${context.teamName}」的反馈`}
        subtitle={`${context.advisorName}老师，您好：下面是这支队伍的脱敏概要（不含成员联系方式），写点方向性建议即可。`}
      >
        <div className="stack-sm">
          <div className="row-tight">
            <Tag tone="primary">{context.eventCode ?? '未绑定赛事'}</Tag>
            {context.roles.map((role, index) => (
              <Tag key={`${role}-${index}`} tone="muted">
                {role}
              </Tag>
            ))}
          </div>
          <p className="field-hint">
            角色分工按已确认成员统计。您的原文将一字不差地展示给全队，并在下方追加一条 AI
            生成的行动建议（由队伍侧生成、与您原文分列，不改动您的任何措辞）。
          </p>
        </div>

        <hr className="divider" />

        <form className="stack" onSubmit={handleSubmit} noValidate>
          {formError !== null ? (
            <div className="banner banner-danger" role="alert">
              <span className="banner-ico" aria-hidden="true">
                !
              </span>
              <span className="wrap-anywhere">{formError}</span>
            </div>
          ) : null}
          <div className="field">
            <label className="field-label" htmlFor="advisor-feedback-content">
              反馈内容（必填，500 字以内；本链接只接受一次提交）
            </label>
            <textarea
              id="advisor-feedback-content"
              className="textarea"
              rows={5}
              value={content}
              maxLength={600}
              placeholder="如：选题方向可行，但材料初稿滞后，建议周三前补齐数据部分并明确分工。"
              onChange={(event) => setContent(event.target.value)}
            />
            <span className="field-hint">{content.trim().length}/500</span>
          </div>
          <Button type="submit" variant="primary" className="btn-block" loading={submit.isPending}>
            {submit.isPending ? '正在提交…' : '提交反馈'}
          </Button>
        </form>
      </Card>
    </div>
  );
}
